import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { RefreshCw, Settings, Database, TrendingUp, TrendingDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface CrmConnection {
  id: number;
  provider: string;
  brandId: number | null;
  siteId: string | null;
  isActive: boolean;
  lastSyncAt: string | null;
  apiKey: string;
}

interface SummaryData {
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
  newCustomers: number;
  totalRefunds: number;
  refundCount: number;
  refundRate: number;
  successfulTransactions: number;
  declinedTransactions: number;
  cancelledOrders: number;
  inquiryCount: number;
  shippingRevenue: number;
  taxCollected: number;
  previousPeriod: {
    changes: {
      totalRevenue: number | null;
      totalOrders: number | null;
      newCustomers: number | null;
      averageOrderValue: number | null;
    };
  };
}

interface DailyRow {
  date: string;
  revenue: number;
  orders: number;
  newCustomers: number;
  refunds: number;
  aov: number;
}

interface TopProduct {
  name: string;
  sku: string;
  totalRevenue: number;
  totalQuantity: number;
  orderCount: number;
}

function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("rounded-lg", className)}
      style={{ background: "linear-gradient(90deg, #EFEFED 25%, #E5E3DF 50%, #EFEFED 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }}
    />
  );
}

function KpiCard({
  label, value, change, prefix = "", suffix = "",
}: {
  label: string;
  value: number | string;
  change?: number | null;
  prefix?: string;
  suffix?: string;
}) {
  const formattedValue = typeof value === "number"
    ? prefix + value.toLocaleString("en-US", { minimumFractionDigits: prefix === "$" ? 2 : 0, maximumFractionDigits: prefix === "$" ? 2 : 0 }) + suffix
    : value;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E5E2DC",
        borderRadius: 12,
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
      data-testid={`crm-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: "#8C8578", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </span>
      <span style={{ fontSize: 24, fontWeight: 700, color: "#1A1A1A", lineHeight: 1.2 }}>
        {formattedValue}
      </span>
      {change !== undefined && change !== null && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            fontSize: 10,
            fontWeight: 600,
            borderRadius: 4,
            padding: "2px 6px",
            alignSelf: "flex-start",
            background: change >= 0 ? "#F0FDF4" : "#FEF2F2",
            color: change >= 0 ? "#16A34A" : "#DC2626",
          }}
        >
          {change >= 0 ? <TrendingUp style={{ width: 10, height: 10 }} /> : <TrendingDown style={{ width: 10, height: 10 }} />}
          {change >= 0 ? "+" : ""}{change.toFixed(1)}%
        </span>
      )}
    </div>
  );
}

function SmallKpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E2DC", borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#8C8578", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: "#1A1A1A" }}>{value}</div>
    </div>
  );
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function formatCurrency(v: number): string {
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface CanvasCrmTabProps {
  onOpenSettings: () => void;
}

export function CanvasCrmTab({ onOpenSettings }: CanvasCrmTabProps) {
  const { startDate, endDate } = useDashboardDate();
  const queryClient = useQueryClient();
  const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(null);

  const { data: connections = [], isLoading: connsLoading } = useQuery<CrmConnection[]>({
    queryKey: ["/api/crm/connections"],
    queryFn: () => fetch("/api/crm/connections").then(r => r.json()),
  });

  const activeConnection = selectedConnectionId
    ? connections.find(c => c.id === selectedConnectionId) ?? connections[0]
    : connections[0];

  const connId = activeConnection?.id;

  const summaryQuery = useQuery<SummaryData>({
    queryKey: ["/api/crm/metrics/summary", connId, startDate, endDate],
    queryFn: () =>
      fetch(`/api/crm/metrics/summary?connectionId=${connId}&startDate=${startDate}&endDate=${endDate}`).then(r => r.json()),
    enabled: !!connId,
  });

  const dailyQuery = useQuery<DailyRow[]>({
    queryKey: ["/api/crm/metrics/daily", connId, startDate, endDate],
    queryFn: () =>
      fetch(`/api/crm/metrics/daily?connectionId=${connId}&startDate=${startDate}&endDate=${endDate}`).then(r => r.json()),
    enabled: !!connId,
  });

  const topProductsQuery = useQuery<TopProduct[]>({
    queryKey: ["/api/crm/metrics/top-products", connId, startDate, endDate],
    queryFn: () =>
      fetch(`/api/crm/metrics/top-products?connectionId=${connId}&startDate=${startDate}&endDate=${endDate}`).then(r => r.json()),
    enabled: !!connId,
  });

  const syncMutation = useMutation({
    mutationFn: () => fetch(`/api/crm/connections/${connId}/sync`, { method: "POST" }).then(r => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/connections"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/metrics/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/metrics/daily"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/metrics/top-products"] });
    },
  });

  const pillBase: React.CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "5px 10px", borderRadius: 7, fontSize: 11.5, fontWeight: 500,
    border: "none", cursor: "pointer", transition: "background 120ms",
  };

  if (connsLoading) {
    return (
      <div className="flex-1 overflow-y-auto p-6" style={{ background: "#F0F0EF" }}>
        <div className="grid grid-cols-4 gap-4 mb-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-52 mb-4" />
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-16" />)}
        </div>
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-12" style={{ background: "#F0F0EF" }} data-testid="crm-empty-state">
        <div style={{ background: "#fff", border: "1px solid #E5E2DC", borderRadius: 16, padding: "40px 32px", textAlign: "center", maxWidth: 400, width: "100%" }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#F0EDEA", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <Database style={{ width: 24, height: 24, color: "#B5B0A8" }} />
          </div>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#1A1A1A", margin: "0 0 8px" }}>Connect your CRM</h2>
          <p style={{ fontSize: 13, color: "#8C8578", margin: "0 0 20px", lineHeight: 1.6 }}>
            Pull real sales data — revenue, orders, customers, refunds — right alongside your ad performance.
          </p>
          <button
            data-testid="crm-connect-btn"
            onClick={onOpenSettings}
            style={{ ...pillBase, background: "#6366F1", color: "#fff", fontSize: 13, padding: "10px 20px", borderRadius: 8, fontWeight: 600 }}
          >
            Connect Admoji CRM
          </button>
        </div>
      </div>
    );
  }

  const summary = summaryQuery.data;
  const daily = dailyQuery.data ?? [];
  const topProducts = topProductsQuery.data ?? [];
  const changes = summary?.previousPeriod?.changes;
  const isDataLoading = summaryQuery.isLoading || dailyQuery.isLoading;
  const hasError = summaryQuery.isError || dailyQuery.isError;

  return (
    <div className="flex-1 overflow-y-auto" style={{ background: "#F0F0EF" }} data-testid="crm-analytics-panel">
      {/* Header */}
      <div
        className="flex-shrink-0 px-6 py-4 border-b flex items-center justify-between"
        style={{ borderColor: "#E5E2DC", background: "#F0F0EF" }}
      >
        <div className="flex items-center gap-3">
          <h2 style={{ fontSize: 15, fontWeight: 600, color: "#1A1A1A", margin: 0 }}>CRM Analytics</h2>
          {connections.length > 1 && (
            <select
              value={selectedConnectionId ?? connId ?? ""}
              onChange={e => setSelectedConnectionId(Number(e.target.value))}
              style={{ fontSize: 12, border: "1px solid #E5E2DC", borderRadius: 6, padding: "3px 8px", color: "#4A4A4A", background: "#fff" }}
              data-testid="crm-connection-selector"
            >
              {connections.map(c => (
                <option key={c.id} value={c.id}>{c.siteId || `Connection ${c.id}`}</option>
              ))}
            </select>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeConnection?.lastSyncAt && (
            <span style={{ fontSize: 11, color: "#8C8578" }}>
              Last synced: {timeAgo(activeConnection.lastSyncAt)}
            </span>
          )}
          {connId && (
            <button
              data-testid="crm-sync-btn"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              style={{ ...pillBase, background: "#F8F8F7", color: "#4A4A4A", border: "1px solid #E5E2DC" }}
              title="Sync now"
            >
              <RefreshCw style={{ width: 13, height: 13, ...(syncMutation.isPending ? { animation: "spin 1s linear infinite" } : {}) }} />
              {syncMutation.isPending ? "Syncing…" : "Sync"}
            </button>
          )}
          <button
            data-testid="crm-settings-btn"
            onClick={onOpenSettings}
            style={{ ...pillBase, background: "#F8F8F7", color: "#4A4A4A", border: "1px solid #E5E2DC" }}
            title="CRM Settings"
          >
            <Settings style={{ width: 13, height: 13 }} />
          </button>
        </div>
      </div>

      <div className="p-6" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {hasError && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 10, padding: "12px 16px", color: "#DC2626", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Unable to load CRM data.</span>
            <button
              onClick={() => { summaryQuery.refetch(); dailyQuery.refetch(); }}
              style={{ fontSize: 12, fontWeight: 500, color: "#DC2626", background: "transparent", border: "1px solid #FCA5A5", borderRadius: 6, padding: "3px 10px", cursor: "pointer" }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Primary KPI Cards */}
        {isDataLoading ? (
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : summary ? (
          <div className="grid grid-cols-2 gap-4" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
            <KpiCard label="Total Revenue" value={summary.totalRevenue} prefix="$" change={changes?.totalRevenue} />
            <KpiCard label="Total Orders" value={summary.totalOrders} change={changes?.totalOrders} />
            <KpiCard label="Avg Order Value" value={summary.averageOrderValue} prefix="$" change={changes?.averageOrderValue} />
            <KpiCard label="New Customers" value={summary.newCustomers} change={changes?.newCustomers} />
          </div>
        ) : null}

        {/* Revenue Chart */}
        {isDataLoading ? (
          <Skeleton className="h-52" />
        ) : daily.length > 0 ? (
          <div style={{ background: "#fff", border: "1px solid #E5E2DC", borderRadius: 12, padding: "16px 20px" }} data-testid="crm-revenue-chart">
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A", marginBottom: 12 }}>Revenue & Orders</div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={daily} margin={{ top: 4, right: 40, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#8C8578" }}
                  tickFormatter={d => {
                    const parts = d.split("-");
                    return `${parts[1]}/${parts[2]}`;
                  }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="revenue"
                  tick={{ fontSize: 10, fill: "#8C8578" }}
                  tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="orders"
                  orientation="right"
                  tick={{ fontSize: 10, fill: "#8C8578" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{ border: "1px solid #E5E2DC", borderRadius: 8, fontSize: 12 }}
                  formatter={(value: number, name: string) => [
                    name === "revenue" ? formatCurrency(value) : value,
                    name === "revenue" ? "Revenue" : "Orders",
                  ]}
                  labelFormatter={label => {
                    const parts = label.split("-");
                    return `${parts[1]}/${parts[2]}/${parts[0]}`;
                  }}
                />
                <Line yAxisId="revenue" type="monotone" dataKey="revenue" stroke="#6366F1" strokeWidth={2} dot={false} name="revenue" />
                <Line yAxisId="orders" type="monotone" dataKey="orders" stroke="#F59E0B" strokeWidth={2} dot={false} name="orders" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : null}

        {/* Secondary KPI Grid */}
        {summary && (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
            <SmallKpiCard label="Refund Rate" value={`${summary.refundRate.toFixed(1)}%`} />
            <SmallKpiCard label="Total Refunds" value={formatCurrency(summary.totalRefunds)} />
            <SmallKpiCard label="Successful" value={summary.successfulTransactions.toLocaleString()} />
            <SmallKpiCard label="Declined" value={summary.declinedTransactions.toLocaleString()} />
            <SmallKpiCard label="Cancelled" value={summary.cancelledOrders.toLocaleString()} />
            <SmallKpiCard label="Inquiries" value={summary.inquiryCount.toLocaleString()} />
            <SmallKpiCard label="Shipping Rev" value={formatCurrency(summary.shippingRevenue)} />
            <SmallKpiCard label="Tax Collected" value={formatCurrency(summary.taxCollected)} />
          </div>
        )}

        {/* Top Products */}
        {isDataLoading ? (
          <Skeleton className="h-48" />
        ) : topProducts.length > 0 ? (
          <div style={{ background: "#fff", border: "1px solid #E5E2DC", borderRadius: 12, overflow: "hidden" }} data-testid="crm-top-products">
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #F0EDEA" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A" }}>Top Products</span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #F0EDEA" }}>
                    {["Product Name", "SKU", "Revenue", "Qty Sold", "Orders"].map(h => (
                      <th key={h} style={{ textAlign: h === "Product Name" ? "left" : "right", padding: "8px 14px", fontSize: 11, fontWeight: 600, color: "#8C8578", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((p, i) => (
                    <tr key={`${p.name}-${p.sku}`} style={{ background: i % 2 === 1 ? "#FAFAF9" : "#fff", borderBottom: "1px solid #F5F3F0" }}>
                      <td style={{ padding: "8px 14px", color: "#1A1A1A", fontWeight: 500, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</td>
                      <td style={{ padding: "8px 14px", color: "#8C8578", textAlign: "right" }}>{p.sku || "—"}</td>
                      <td style={{ padding: "8px 14px", color: "#1A1A1A", textAlign: "right", fontWeight: 600 }}>{formatCurrency(p.totalRevenue)}</td>
                      <td style={{ padding: "8px 14px", color: "#4A4A4A", textAlign: "right" }}>{p.totalQuantity.toLocaleString()}</td>
                      <td style={{ padding: "8px 14px", color: "#4A4A4A", textAlign: "right" }}>{p.orderCount.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : summary ? (
          <div style={{ background: "#fff", border: "1px solid #E5E2DC", borderRadius: 12, padding: "24px", textAlign: "center", color: "#8C8578", fontSize: 13 }}>
            No product data available for this period.
          </div>
        ) : null}
      </div>
    </div>
  );
}
